/**
 * @fileoverview WebSocket middleware for chat streaming
 * Connects Socket.IO WebSocket events to Redux store
 * Global app-wide WebSocket connection, not tied to specific chats
 *
 * **Architecture**: Registry-based O(1) routing to multiple Redux stores
 * - Single WebSocket connection for entire app
 * - O(1) Map lookup routes events to correct store (playground/embed/inbox)
 * - Supports 100+ tokens/sec streaming without performance degradation
 *
 * **Routing**, through the three helpers beside this file:
 * ```
 * WebSocket Event
 *   ↓
 * Chat Registry (chatRegistry.ts, O(1) chatId → storeType lookup)
 *   ↓
 * Streaming Config (config.ts, should we stream this token?)
 *   ↓
 * Action Mapping (actionMapping.ts, event → Redux action type)
 *   ↓
 * Redux Store Dispatch
 * ```
 */

import {
  type ChatUpdatedPayload,
  type CodeInterpreterBlockCompletePayload,
  type CodeInterpreterBlockStartPayload,
  type CodeInterpreterBlockUpdatePayload,
  type CodeInterpreterCompletePayload,
  type CodeInterpreterErrorPayload,
  type ConnectedPayload,
  type MessageCompletePayload,
  type MessageErrorPayload,
  type MessageTokenPayload,
  type NexusWebSocketGetter
} from "@nexus/types"
import {
  ChatMetadataEvents,
  ChatRoomEvents,
  CodeInterpreterEvents,
  ConnectionEvents,
  type JoinChatPayload,
  MessageEvents,
  OrganizationEvents
} from "@nexus/types/core"
import type { Middleware } from "@reduxjs/toolkit"
import { io, Socket } from "socket.io-client"

import { getBackendUrl } from "@/common/services/chat"
// The thunk itself, not its action-type string: `.fulfilled.match` is a typed
// guard, so the payload needs no cast and the type string cannot drift.
import {
  embedChatActions,
  reopenClosedEmbedChat,
  sendEmbedMessage
} from "@/modules/EmbedChat/store"

import { getActionType, type WsEventType } from "./actionMapping"
import { getStoreType, registerChat } from "./chatRegistry"
import { shouldDispatchStreamingToken } from "./config"
import {
  CHAT_STREAMING_UNAUTHORIZED_MESSAGE,
  type ChatStreamingHandshakeAuth,
  isChatStreamingHandshakeRejected,
  roomsToRequestOnReady
} from "./handshake"
import { setConnected, setConnectionDetails, setError } from "./websocketSlice"

const { clearEmbedStreamingMessage } = embedChatActions

/**
 * `playgroundChat/sendMessage/fulfilled`, matched by its action type rather
 * than by the thunk the way its embed twin is. The playground thunk lives in
 * `apps/frontend`; this package is imported BY that app and cannot import back
 * out of it, so the type string is the only handle available here — the same
 * one every other playground branch in this middleware already uses.
 */
const isPlaygroundSendFulfilled = (
  action: unknown
): action is { payload: { chatId?: string; wasNewChatroom?: boolean } } =>
  typeof action === "object" &&
  action !== null &&
  "type" in action &&
  (action as { type: unknown }).type === "playgroundChat/sendMessage/fulfilled" &&
  "payload" in action &&
  typeof (action as { payload: unknown }).payload === "object" &&
  (action as { payload: unknown }).payload !== null

/** The embed store's instance for this chat, or `null` when it holds none. */
const findEmbedChat = (state: unknown, chatId: string): object | null => {
  if (typeof state !== "object" || state === null || !("embedChat" in state)) return null

  const embedChat: unknown = state.embedChat
  if (typeof embedChat !== "object" || embedChat === null || !("chats" in embedChat)) return null

  const chats: unknown = embedChat.chats
  if (typeof chats !== "object" || chats === null || !(chatId in chats)) return null

  const chat: unknown = Reflect.get(chats, chatId)
  return typeof chat === "object" && chat !== null ? chat : null
}

/**
 * Whether the embed store HOLDS this chat — i.e. its reducer adopted it.
 *
 * The room decision reads this rather than the action's payload: a second,
 * concurrent `reopenClosedEmbedChat` of the same closed chat is ignored by the
 * reducer (`if (!closed) return`), and its payload can name a different reserved
 * id. Joining that would point the socket at a room no selector reads from.
 */
const embedStoreHoldsChat = (state: unknown, chatId: string): boolean =>
  findEmbedChat(state, chatId) !== null

/**
 * The widget SESSION an embed chat belongs to — the session id it was opened
 * under, which the server stored as the chat's thread id — or `null` for a chat
 * the embed store does not hold. A playground or inbox chat joins as a signed-in
 * user and states no session.
 *
 * Read off the CHAT rather than off "the current session": one widget switches
 * session in place ("new conversation", "pick a past conversation") on the same
 * socket, and a reconnect re-joins whichever chat is current — so the session to
 * state is the one that chat was opened under, never whichever one is newest.
 */
const embedSessionOfChat = (state: unknown, chatId: string): string | null => {
  const chat = findEmbedChat(state, chatId)
  if (chat === null || !("sessionId" in chat)) return null
  return typeof chat.sessionId === "string" && chat.sessionId.length > 0 ? chat.sessionId : null
}

/**
 * Shortest gap between two identity resolutions while the socket is still
 * waiting for one. Identity arrives asynchronously and out of band — a Clerk
 * token once `initializeAuth` has run, an agent id once the embed deployment
 * has been fetched — and no single action reliably marks the moment for both
 * apps. So the middleware re-checks off its own action stream, and this
 * throttle is what stops that from becoming a token lookup per dispatch.
 */
const IDENTITY_RETRY_INTERVAL_MS = 500

export interface WebSocketMiddlewareOptions {
  /**
   * Resolves the caller's Clerk JWT, or `null` when nobody is signed in.
   *
   * `apps/frontend` passes its Clerk accessor, so a dashboard connection
   * authenticates as a real user. The standalone embed SPA and the frontend's public
   * `/embed/:id` route pass nothing — those visitors have no user, and their
   * identity is the EMBED deployment's agent id, which the middleware picks
   * up from the embed initialization result.
   */
  getAuthToken?: () => Promise<string | null> | string | null
}

/**
 * WebSocket middleware with registry-based routing
 * Manages global Socket.IO connection and routes events to correct Redux store
 *
 * **Key Features**:
 * - Single WebSocket connection (app-wide, not per-chat)
 * - O(1) routing via chatId → storeType registry
 * - Handles multiple stores: playground, embed, inbox
 * - Supports 100+ tokens/sec streaming without lag
 *
 * **Connection is deferred until an identity exists.** The server refuses a
 * handshake it cannot identify, so connecting eagerly — as this middleware
 * used to, at the first dispatched action — would now be refused every time
 * on the embed, whose agent id is only known after the deployment has been
 * fetched. `autoConnect` is therefore off and `socket.connect()` is called
 * once, when the first usable identity resolves.
 *
 * @returns Redux middleware function
 */
export const createWebSocketMiddleware = ({
  getAuthToken
}: WebSocketMiddlewareOptions = {}): Middleware => {
  let socket: Socket | null = null
  let currentChatId: string | null = null
  let currentOrganizationId: string | null = null
  let isInitialized = false

  /**
   * Public agent id of the EMBED deployment this store is chatting with.
   * Learned from the embed initialization result, which both the standalone embed SPA and
   * the frontend's `/embed/:id` route dispatch through the same shared thunk.
   */
  let embedAgentId: string | null = null

  /** `socket.connect()` has been called — socket.io owns retries from here. */
  let connectRequested = false
  /** The server refused the handshake. Terminal: retrying cannot fix it. */
  let handshakeRejected = false
  /** An identity resolution is in flight; do not start a second one. */
  let identityResolving = false
  let lastIdentityAttemptAt = 0

  /**
   * Redux `getState`, captured when the middleware is applied. Held here
   * rather than closing over `store` so the helpers below sit beside the
   * connection state they read, at the same scope as `socket` itself.
   */
  let readState: () => unknown = () => undefined

  /**
   * Every `chat:room:join` this middleware emits goes through here, so an embed
   * chat's join always states the session that created it — the server admits an
   * embed visitor only to its own session's chats. `sessionId` is passed where
   * the action already carries it; otherwise it is read off the embed store.
   *
   * 🔴 An EMBED chat whose session cannot be read is NOT joined at all. That is
   * the state a session switch leaves behind: `removeEmbedSession` deletes the old
   * chat from the store while `currentChatId` still names it, so a reconnect
   * before the next `initialize` would otherwise re-join it with no session. A
   * session-less join of a chat the store no longer holds streams to nothing on
   * screen, and on the server it is indistinguishable from an OLD widget client —
   * the population the observe-mode `missing-session` line exists to count. With
   * this rule that line counts old clients and nothing else.
   *
   * A chat that was never an embed chat (playground, inbox) has no session to
   * state and joins as a signed-in user, unchanged.
   */
  const emitJoin = (
    chatId: string,
    sessionId: string | null = embedSessionOfChat(readState(), chatId)
  ): void => {
    if (sessionId === null && getStoreType(chatId) === "embed") return
    const payload: JoinChatPayload = sessionId === null ? { chatId } : { chatId, sessionId }
    socket?.emit(ChatRoomEvents.JOIN, payload)
  }

  /**
   * Presence labels off Redux. Cosmetic only — the server never reads them
   * for authorization. The standalone embed SPA has no `auth` slice at all, hence the
   * guard rather than a direct read.
   */
  const readPresenceLabels = (): Pick<ChatStreamingHandshakeAuth, "userName" | "avatarUrl"> => {
    const state = readState()
    const hasAuth = (
      s: unknown
    ): s is { auth?: { userName?: string | null; avatarUrl?: string | null } } =>
      typeof s === "object" && s !== null && "auth" in s

    if (!hasAuth(state)) {
      return {}
    }
    return {
      ...(state.auth?.userName ? { userName: state.auth.userName } : {}),
      ...(state.auth?.avatarUrl ? { avatarUrl: state.auth.avatarUrl } : {})
    }
  }

  /**
   * The handshake payload, or `null` when this store cannot yet say who it is.
   * Null means "do not connect" — never "connect anonymously", which is what
   * the old `token: authToken || "anonymous"` did and what the server refuses.
   */
  const resolveHandshakeAuth = async (): Promise<ChatStreamingHandshakeAuth | null> => {
    let token: string | null = null
    if (getAuthToken) {
      try {
        token = await getAuthToken()
      } catch {
        // A token lookup that throws is the same as no token: fall through to
        // the embed identity, then to "not identified yet".
        token = null
      }
    }

    if (token) {
      return { token, ...readPresenceLabels() }
    }
    if (embedAgentId) {
      return { agentId: embedAgentId, ...readPresenceLabels() }
    }
    return null
  }

  /**
   * Connect as soon as an identity exists, and at most once. Returns
   * synchronously when there is nothing to do, because it runs off every
   * dispatched action until the socket is up.
   */
  const ensureConnected = (): void => {
    if (!socket || connectRequested || handshakeRejected || identityResolving) {
      return
    }
    const now = Date.now()
    if (now - lastIdentityAttemptAt < IDENTITY_RETRY_INTERVAL_MS) {
      return
    }
    lastIdentityAttemptAt = now
    identityResolving = true

    void resolveHandshakeAuth()
      .then((auth) => {
        if (!auth || !socket || connectRequested || handshakeRejected) {
          return
        }
        connectRequested = true
        socket.connect()
      })
      .finally(() => {
        identityResolving = false
      })
  }

  // Expose socket instance for features that need direct access (e.g., file upload progress)
  // This is intentionally a closure variable accessed via the getter
  const socketGetter: NexusWebSocketGetter = {
    getSocket: () => socket
  }

  // Store reference globally for hooks to access
  // Use globalThis for universal access (works in browser, Node, workers, etc.)
  if (typeof globalThis !== "undefined") {
    ;(
      globalThis as typeof globalThis & { __nexusWebSocket?: NexusWebSocketGetter }
    ).__nexusWebSocket = socketGetter
  }

  return (store) => (next) => (action: unknown) => {
    // Initialize global WebSocket connection immediately (app-wide)
    if (!isInitialized) {
      readState = store.getState
      // Create the connection with AGGRESSIVE reconnection config. It stays
      // closed until `ensureConnected` has an identity to hand the server.
      //
      // The backend URL is read HERE rather than in the factory body above.
      // `getBackendUrl()` refuses when `initializeEnvironment()` has not run,
      // and CONSTRUCTING this middleware needs no URL — only opening the socket
      // does. Reading it eagerly made merely building a store depend on
      // configuration it does not yet use, which is a wider contract than the
      // behaviour requires. Read at the point of use, the refusal lands on the
      // action that actually needs a host.
      socket = io(`${getBackendUrl()}/chat-streaming`, {
        transports: ["websocket", "polling"],
        autoConnect: false,

        // ROBUST reconnection configuration - never give up!
        reconnection: true,
        reconnectionAttempts: Infinity, // Never stop trying!
        reconnectionDelay: 1000, // Start with 1 second
        reconnectionDelayMax: 30000, // Max 30 seconds between attempts
        randomizationFactor: 0.5,
        timeout: 20000,

        // Callback form, so every reconnection re-reads the identity rather
        // than replaying a payload captured at store-construction time. That
        // is what keeps a rotated Clerk token working across a reconnect.
        auth: (callback: (data: ChatStreamingHandshakeAuth) => void) => {
          void resolveHandshakeAuth().then((auth) => callback(auth ?? {}))
        }
      })

      // Connection events
      socket.on("connect", () => {
        store.dispatch(setConnected(true))
        if (socket?.id) {
          store.dispatch(setConnectionDetails({ socketId: socket.id }))
        }

        // CRITICAL: Rejoin rooms on reconnection!
        if (currentChatId && socket) {
          emitJoin(currentChatId)
        }
        if (currentOrganizationId && socket) {
          // No organizationId: the server derives the room from the identity
          // it verified at the handshake and ignores anything sent here.
          socket.emit(OrganizationEvents.JOIN, { type: "playground" })
        }
      })

      socket.on("disconnect", (reason: string) => {
        if (handshakeRejected) {
          // We tore the socket down ourselves after the server refused the
          // handshake. Its terminal error is already in the store; the
          // "io client disconnect" reason must not overwrite it.
          return
        }
        store.dispatch(setConnected(false))
        store.dispatch(setError(reason || "Connection lost"))
      })

      socket.on("connect_error", (error: Error) => {
        if (!isChatStreamingHandshakeRejected(error)) {
          // A transport failure. Socket.IO retries and usually wins, so
          // surfacing an error here would flash a message the user cannot
          // act on. `reconnect_failed` below is what reports giving up.
          return
        }

        // The server refused to identify this connection. socket.io destroys
        // the socket on a namespace rejection and never retries it, so
        // `reconnect_failed` — the one path that used to set a terminal
        // error — cannot fire. The old empty handler therefore left chat
        // permanently dead and indistinguishable from idle. Say so instead.
        handshakeRejected = true
        // Belt and braces: also tear down the manager, so nothing can bring
        // the same refused identity back up behind the error we just set.
        socket?.disconnect()

        // Order matters: `setConnected(false)` clears `error` in the slice,
        // so the message has to be dispatched after it, not before.
        store.dispatch(setConnected(false))
        store.dispatch(
          setConnectionDetails({
            reconnectAttempt: 0,
            isReconnecting: false,
            permanentlyDisconnected: true
          })
        )
        store.dispatch(setError(CHAT_STREAMING_UNAUTHORIZED_MESSAGE))
      })

      // Reconnection attempt tracking
      socket.io.on("reconnect_attempt", (attemptNumber: number) => {
        store.dispatch(
          setConnectionDetails({
            reconnectAttempt: attemptNumber,
            isReconnecting: true
          })
        )
      })

      // Successful reconnection
      socket.io.on("reconnect", (_attemptNumber: number) => {
        store.dispatch(setError(null))
        store.dispatch(
          setConnectionDetails({
            reconnectAttempt: 0,
            isReconnecting: false
          })
        )
      })

      // Reconnection failed (max attempts reached)
      socket.io.on("reconnect_failed", () => {
        store.dispatch(setConnected(false))
        store.dispatch(
          setConnectionDetails({
            reconnectAttempt: 0,
            isReconnecting: false,
            permanentlyDisconnected: true
          })
        )
        store.dispatch(
          setError("Unable to connect to server. Please check your connection and refresh.")
        )
      })

      socket.on(ConnectionEvents.READY, (data: ConnectedPayload) => {
        store.dispatch(
          setConnectionDetails({
            userId: data.userId,
            clientId: data.clientId,
            socketId: data.clientId // clientId is the socket ID
          })
        )

        // ONE decision for BOTH rooms. The server derives every room from the
        // identity it verified at the handshake and refuses an anonymous embed
        // visitor outright, so asking anyway is a guaranteed-refused round trip
        // plus an unactionable server warning on every connect and reconnect.
        // `data.organizationId` is NOT the discriminator — an embed visitor now
        // carries a real, verified one — which is exactly how these two branches
        // came to disagree. See roomsToRequestOnReady.
        const rooms = roomsToRequestOnReady(data)

        if (socket && rooms.user && data.userId) {
          socket.emit("user:join", { userId: data.userId })
        }

        if (socket && rooms.organization && data.organizationId) {
          currentOrganizationId = data.organizationId
          socket.emit(OrganizationEvents.JOIN, { type: "playground" })
        }
      })

      socket.on(ChatRoomEvents.JOINED, (_data: { chatId: string }) => {
        // Room joined successfully - chatId is already in registry
      })

      /**
       * Dispatch WebSocket event to owning Redux store
       * Uses O(1) registry lookup + streaming config for optimal performance
       *
       * **Performance Optimization**:
       * - Skips streaming tokens for inactive chats (when configured)
       * - Reduces Redux overhead by 50-90% for multi-chat views
       * - Active chat still gets smooth token streaming
       *
       * @param eventType - Type of event (streaming/completed/failed)
       * @param chatId - Chat ID to dispatch for
       * @param payload - Event payload to dispatch
       */
      const dispatchToStore = (eventType: WsEventType, chatId: string, payload: unknown): void => {
        const targetStore = getStoreType(chatId)

        if (!targetStore) {
          return
        }

        // Performance optimization: Skip streaming tokens for inactive chats
        if (eventType === "streaming") {
          const isActive = chatId === currentChatId
          const shouldStream = shouldDispatchStreamingToken(targetStore, isActive)

          if (!shouldStream) {
            return
          }
        }

        const actionType = getActionType(eventType, targetStore)
        store.dispatch({ type: actionType, payload })
      }

      // Streaming events
      socket.on(MessageEvents.STREAMING, ({ chatId, messageId, token }: MessageTokenPayload) => {
        // Use chatId from payload (not global currentChatId) for correct chat isolation
        dispatchToStore("streaming", chatId, {
          chatId,
          messageId,
          token
        })
      })

      socket.on(
        MessageEvents.COMPLETED,
        ({
          chatId: payloadChatId,
          messageId,
          content,
          metaConversationId,
          contentType,
          toolType,
          toolCalls,
          toolCallId,
          name,
          endsTurn
        }: MessageCompletePayload) => {
          const resolvedChatId = payloadChatId ?? currentChatId
          if (resolvedChatId) {
            dispatchToStore("completed", resolvedChatId, {
              chatId: resolvedChatId,
              messageId,
              content,
              ...(metaConversationId && { metaConversationId }),
              ...(contentType && { contentType }),
              ...(toolType && { toolType }),
              ...(toolCalls && { toolCalls }),
              ...(toolCallId && { toolCallId }),
              ...(name && { name }),
              // NEX-4252: a card ended the turn — no AI message will follow.
              ...(endsTurn === true && { endsTurn: true })
            })

            // Clear streaming state after brief delay to ensure smooth transition
            if (typeof globalThis !== "undefined" && globalThis.setTimeout) {
              globalThis.setTimeout(() => {
                const targetStore = getStoreType(resolvedChatId)
                if (targetStore) {
                  if (targetStore === "embed") {
                    store.dispatch(
                      clearEmbedStreamingMessage({
                        chatId: resolvedChatId,
                        messageId
                      })
                    )
                  } else if (targetStore === "playground") {
                    store.dispatch({
                      type: "playgroundChat/clearStreamingMessage",
                      payload: { chatId: resolvedChatId, messageId }
                    })
                  }
                }
              }, 500)
            }
          }
        }
      )

      socket.on(
        MessageEvents.FAILED,
        ({ chatId: payloadChatId, messageId, error }: MessageErrorPayload) => {
          const resolvedChatId = payloadChatId ?? currentChatId

          if (resolvedChatId) {
            dispatchToStore("failed", resolvedChatId, {
              chatId: resolvedChatId,
              messageId,
              error: {
                code: error.code,
                message: error.message
              }
            })

            const targetStore = getStoreType(resolvedChatId)
            if (targetStore === "embed") {
              store.dispatch(
                clearEmbedStreamingMessage({
                  chatId: resolvedChatId,
                  messageId
                })
              )
            } else if (targetStore === "playground") {
              store.dispatch({
                type: "playgroundChat/clearStreamingMessage",
                payload: { chatId: resolvedChatId, messageId }
              })
            }
          }
        }
      )

      socket.on(ChatMetadataEvents.UPDATED, ({ chatId, updates }: ChatUpdatedPayload) => {
        // Handle chat metadata updates (topic, status, etc.)
        if (updates.topic) {
          const targetStore = getStoreType(chatId)
          if (targetStore === "playground") {
            store.dispatch({
              type: "playgroundChat/updateChatTopic",
              payload: { chatId, topic: updates.topic as string }
            })
          } else if (targetStore === "embed") {
            store.dispatch({
              type: "embedChat/updateEmbedChatTopic",
              payload: { chatId, topic: updates.topic as string }
            })
          }
        }
      })

      // =====================================================
      // Code Interpreter Streaming Events
      // Routes to unified streamingMessages.blocks state via ACTION_MAP
      // =====================================================

      socket.on(
        CodeInterpreterEvents.BLOCK_START,
        ({ messageId, block }: CodeInterpreterBlockStartPayload) => {
          if (currentChatId) {
            dispatchToStore("blockStart", currentChatId, {
              chatId: currentChatId,
              messageId,
              block
            })
          }
        }
      )

      socket.on(
        CodeInterpreterEvents.BLOCK_UPDATE,
        ({ messageId, blockId, content }: CodeInterpreterBlockUpdatePayload) => {
          if (currentChatId) {
            dispatchToStore("blockUpdate", currentChatId, {
              chatId: currentChatId,
              messageId,
              blockId,
              content
            })
          }
        }
      )

      socket.on(
        CodeInterpreterEvents.BLOCK_COMPLETE,
        ({ messageId, blockId, output, isError }: CodeInterpreterBlockCompletePayload) => {
          if (currentChatId) {
            dispatchToStore("blockComplete", currentChatId, {
              chatId: currentChatId,
              messageId,
              blockId,
              output,
              isError
            })
          }
        }
      )

      socket.on(
        CodeInterpreterEvents.EXECUTION_COMPLETE,
        ({ messageId, content, files }: CodeInterpreterCompletePayload) => {
          if (currentChatId) {
            dispatchToStore("codeInterpreterComplete", currentChatId, {
              chatId: currentChatId,
              messageId,
              content,
              files
            })
          }
        }
      )

      socket.on(
        CodeInterpreterEvents.EXECUTION_ERROR,
        ({ messageId, error }: CodeInterpreterErrorPayload) => {
          if (currentChatId) {
            dispatchToStore("codeInterpreterError", currentChatId, {
              chatId: currentChatId,
              messageId,
              error
            })
          }
        }
      )

      socket.on("error", (err: unknown) => {
        // Dispatch error to Redux
        const errorMessage =
          err instanceof Error ? err.message : String(err || "Unknown socket error")
        store.dispatch(setError(errorMessage))
      })

      isInitialized = true
    }

    // Identity arrives asynchronously (Clerk token, or the embed deployment's
    // agent id). Re-check on every action until the socket is up; the call is
    // throttled and no-ops once `connect()` has been requested.
    ensureConnected()

    // `auth/setUserInfo` no longer touches `socket.auth`. The handshake `auth`
    // callback reads the presence labels out of Redux on every (re)connection,
    // so there is nothing left to keep in sync by hand.

    // Handle setOrganizationId - join org room for chat:updated events
    if (
      typeof action === "object" &&
      action !== null &&
      "type" in action &&
      action.type === "auth/setOrganizationId"
    ) {
      const payload = "payload" in action ? action.payload : null
      const orgId = typeof payload === "string" ? payload : null

      if (orgId && socket && !currentOrganizationId) {
        currentOrganizationId = orgId
        socket.emit(OrganizationEvents.JOIN, { type: "playground" })
      }
    }

    // Handle openChat action - register chat and join room
    // Playground chats
    if (
      typeof action === "object" &&
      action !== null &&
      "type" in action &&
      action.type === "playgroundChat/openChat"
    ) {
      const payload = "payload" in action ? (action.payload as { chatId: string }) : null
      if (!payload) return next(action)

      const { chatId: newChatId } = payload

      // Register chat with playground store
      if (newChatId) {
        registerChat(newChatId, "playground")
      }

      // If switching chats, leave old room and join new one
      if (newChatId !== currentChatId) {
        if (socket && currentChatId) {
          socket.emit(ChatRoomEvents.LEAVE, { chatId: currentChatId })
        }

        if (socket && newChatId) {
          emitJoin(newChatId)
        }

        currentChatId = newChatId
      }
    }

    // Embed chats - Initialize (must check BEFORE calling next)
    if (
      typeof action === "object" &&
      action !== null &&
      "type" in action &&
      action.type === "embedChat/initialize/fulfilled"
    ) {
      const payload =
        "payload" in action
          ? (action.payload as {
              chatId: string
              deployment?: { agent?: { id?: string } | null } | null
            })
          : null

      // Call next() first to let Redux process it
      const result = next(action)

      // Then handle WebSocket room joining
      if (payload) {
        const { chatId: newChatId } = payload

        // The embed visitor's whole identity. This is the same agent id the
        // HTTP layer sends as `x-agent-id`, and it is the only thing the
        // server can use to place an anonymous visitor in an organization —
        // so it is captured before any attempt to connect.
        const agentId = payload.deployment?.agent?.id
        if (agentId && agentId !== embedAgentId) {
          embedAgentId = agentId
          // The identity genuinely changed, so the retry throttle must not
          // swallow this attempt — it is the one that brings the embed up.
          lastIdentityAttemptAt = 0
        }

        // Register chat with embed store
        if (newChatId) {
          registerChat(newChatId, "embed")

          // Join the chat room for streaming. Emits made before the handshake
          // completes are buffered by socket.io, and the `connect` handler
          // re-joins `currentChatId` anyway, so this is safe while the
          // connection is still coming up.
          if (socket && newChatId) {
            emitJoin(newChatId)
            currentChatId = newChatId
          }
        }
      }

      // An agent id may have just appeared — try to connect before returning.
      ensureConnected()

      return result
    }

    // Embed chats — the FIRST message is what creates the chatroom row, so the
    // join emitted at `initialize` was refused and has to be made again here.
    //
    // `GET` chatroom RESERVES an id in the deployment cache without writing a
    // `Chat` row, and the server resolves room ownership with a real chat lookup
    // BEFORE joining — so the join emitted at `embedChat/initialize/fulfilled`
    // comes back `CHAT_NOT_FOUND` and is skipped. Nothing re-joined afterwards:
    // the socket re-joins only on `connect`, i.e. on a reconnect or a page
    // reload. That is why the agent's reply never rendered until the visitor
    // refreshed the page — every `message:streaming` / `message:completed` was
    // broadcast to a room this widget had never been admitted to.
    //
    // The emit is UNCONDITIONAL rather than routed through `chat/joinChatRoom`:
    // that path is guarded by `roomToJoin !== currentChatId`, and `currentChatId`
    // already holds this id from the REFUSED join, so dispatching it would be a
    // silent no-op — the widget would stay outside the room and look fixed.
    if (sendEmbedMessage.fulfilled.match(action)) {
      const { chatId, wasNewChatroom } = action.payload

      if (socket && wasNewChatroom && chatId) {
        // The session the message was SENT under, off the thunk's own argument:
        // this branch runs before the reducer has seen the action.
        emitJoin(chatId, action.meta.arg.sessionId)
        currentChatId = chatId
      }
    }

    // Embed chats — a conversation deleted under an open tab was swapped for a
    // fresh one. The fresh id must be REGISTERED, or its streaming frames route to
    // no store at all, and joined. A reserved id's join is refused like the one at
    // initialize, and the first-message re-join above repairs it exactly as it does
    // there; a resumed chat that already has a row is admitted here.
    //
    // `next` runs FIRST and the decision is read off the STORE, never off the
    // payload: the reducer ignores a second, concurrent reopen of the same closed
    // chat, whose payload can name a reserved id the store never adopted.
    if (reopenClosedEmbedChat.fulfilled.match(action)) {
      const { closedChatId, chatId } = action.payload
      const result = next(action)

      if (embedStoreHoldsChat(readState(), chatId)) {
        registerChat(chatId, "embed")

        if (socket && currentChatId === closedChatId) {
          socket.emit(ChatRoomEvents.LEAVE, { chatId: closedChatId })
        }
        if (socket) {
          emitJoin(chatId)
          currentChatId = chatId
        }
      }

      return result
    }

    // Playground chats — the SAME trap as the embed above, on the dashboard side.
    //
    // `/app/chats/new` pre-generates the chat id in the browser and dispatches
    // `playgroundChat/openChat` with it, so the join emitted there names a room
    // whose `Chat` row does not exist yet: the gateway resolves room ownership
    // with a real chat lookup and answers `CHAT_NOT_FOUND`. The row is written
    // by the POST that carries the FIRST message.
    //
    // Nothing re-joined afterwards. The URL replace that follows re-dispatches
    // `openChat` with the SAME id, and that branch is guarded by
    // `newChatId !== currentChatId` — false, because `currentChatId` already
    // holds this id from the REFUSED join — so it is a silent no-op. Every
    // `message:streaming` and `message:completed` of that first turn was
    // therefore broadcast to a room the tab had never been admitted to, and the
    // feed sat on "<agent> is typing…" for the whole page life while a
    // PROCESSED, customer-visible reply sat in the database.
    //
    // Emitted here directly rather than dispatched through `chat/joinChatRoom`
    // for that same reason: that path carries the identical guard, so it would
    // no-op — the tab would stay outside the room and the fix would look applied.
    if (isPlaygroundSendFulfilled(action)) {
      const { chatId, wasNewChatroom } = action.payload

      if (socket && wasNewChatroom && chatId) {
        emitJoin(chatId)
        currentChatId = chatId
      }
    }

    // Embed chats - Manual open (legacy)
    if (
      typeof action === "object" &&
      action !== null &&
      "type" in action &&
      action.type === "embedChat/openEmbedChat"
    ) {
      const payload =
        "payload" in action
          ? (action.payload as {
              chatId: string
              sessionId?: string
              context: { type: "embedded" }
            })
          : null
      if (!payload) return next(action)

      const { chatId: newChatId, sessionId, context } = payload

      // Register chat with embed store
      if (newChatId) {
        registerChat(newChatId, "embed")
      }

      // Skip WebSocket join for embed chats (chatroom doesn't exist yet)
      if (context?.type === "embedded") {
        // Don't join room - chatroom will be created on first message
        return next(action)
      }

      // If switching chats, leave old room and join new one
      if (newChatId !== currentChatId) {
        if (socket && currentChatId) {
          socket.emit(ChatRoomEvents.LEAVE, { chatId: currentChatId })
        }

        if (socket && newChatId) {
          // This branch runs before the reducer has adopted the chat, so the store
          // cannot answer for its session yet — the action can.
          emitJoin(newChatId, sessionId)
        }

        currentChatId = newChatId
      }
    }

    // Handle setActiveChat action - join the room for the active chat
    // Works with both playgroundChat/setActiveChat and embedChat/setActiveEmbedChat
    if (
      typeof action === "object" &&
      action !== null &&
      "type" in action &&
      (action.type === "playgroundChat/setActiveChat" ||
        action.type === "embedChat/setActiveEmbedChat")
    ) {
      const payload = "payload" in action ? (action.payload as { chatId: string }) : null
      const newChatId =
        typeof payload === "object" && payload !== null && "chatId" in payload
          ? payload.chatId
          : payload

      // Determine store type and register if not already registered
      if (newChatId) {
        const storeType = action.type === "playgroundChat/setActiveChat" ? "playground" : "embed"
        const existingStore = getStoreType(newChatId)

        if (!existingStore) {
          registerChat(newChatId, storeType)
        }
      }

      // If switching chats, leave old room and join new one
      if (newChatId !== currentChatId) {
        if (socket && currentChatId) {
          socket.emit(ChatRoomEvents.LEAVE, { chatId: currentChatId })
        }

        if (socket && newChatId) {
          emitJoin(newChatId)
        }

        currentChatId = newChatId
      }
    }

    // ✅ PHASE 3: Handle explicit joinChatRoom action (dispatched during handshake)
    // This allows frontend to JOIN socket BEFORE first message to catch streaming
    if (
      typeof action === "object" &&
      action !== null &&
      "type" in action &&
      action.type === "chat/joinChatRoom" &&
      "payload" in action
    ) {
      const payload = action.payload as { chatId: string }
      const roomToJoin = payload.chatId

      // Only join if switching to a different room (prevent duplicate JOINs)
      if (roomToJoin !== currentChatId) {
        // Leave current room if we're in one
        if (socket && currentChatId) {
          socket.emit(ChatRoomEvents.LEAVE, { chatId: currentChatId })
        }

        // Join the new room
        if (socket && roomToJoin) {
          emitJoin(roomToJoin)
          currentChatId = roomToJoin
        }
      }
    }

    return next(action)
  }
}
